import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type React from 'react';

interface Props {
  codeEdit: string;
}

const TextEdit: React.FC<Props> = ({ codeEdit }) => {
  // Regex to match <old_code>...</old_code> and <new_code>...</new_code> blocks
  const codeBlockRegex =
    /(<old_[a-zA-Z0-9]+>[\s\S]*?<\/old_[a-zA-Z0-9]+>|<new_[a-zA-Z0-9]+>[\s\S]*?<\/new_[a-zA-Z0-9]+>)/g;

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let elementIndex = 0;

  // Find all code blocks
  let match = codeBlockRegex.exec(codeEdit);
  while (match !== null) {
    // Add normal text before the code block
    if (match.index > lastIndex) {
      const normalText = codeEdit.slice(lastIndex, match.index);
      if (normalText.trim()) {
        elements.push(
          <Box key={`normal-${elementIndex}`} sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {normalText}
            </Typography>
          </Box>
        );
        elementIndex += 1;
      }
    }

    const blockContent = match[1];

    if (blockContent.startsWith('<old_')) {
      // Extract content between <old_code> and </old_code>
      const oldCodeContent = blockContent.replace(/<old_[a-zA-Z0-9]+>|<\/old_[a-zA-Z0-9]+>/g, '').trim();
      if (oldCodeContent) {
        elements.push(
          <Box
            key={`old-${elementIndex}`}
            sx={{
              mb: 1,
              p: 1,
              bgcolor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
              - Código a eliminar:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                color: '#d32f2f',
              }}
            >
              {oldCodeContent}
            </Typography>
          </Box>
        );
        elementIndex += 1;
      }
    } else if (blockContent.startsWith('<new_')) {
      // Extract content between <new_code> and </new_code>
      const newCodeContent = blockContent.replace(/<new_[a-zA-Z0-9]+>|<\/new_[a-zA-Z0-9]+>/g, '').trim();
      if (newCodeContent) {
        elements.push(
          <Box
            key={`new-${elementIndex}`}
            sx={{
              mb: 1,
              p: 1,
              bgcolor: '#e8f5e8',
              border: '1px solid #4caf50',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: '#388e3c', fontWeight: 'bold' }}>
              + Código nuevo:
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(newCodeContent).catch(console.error);
              }}
              sx={{
                ml: 1,
                color: '#388e3c',
                fontSize: '0.75rem',
              }}
            >
              Copiar
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  color: '#388e3c',
                  flex: 1,
                }}
              >
                {newCodeContent}
              </Typography>
            </Box>
          </Box>
        );
        elementIndex += 1;
      }
    }

    lastIndex = match.index + match[0].length;
    match = codeBlockRegex.exec(codeEdit);
  }

  // Add any remaining normal text after the last code block
  if (lastIndex < codeEdit.length) {
    const remainingText = codeEdit.slice(lastIndex);
    if (remainingText.trim()) {
      elements.push(
        <Box key={`normal-${elementIndex}`}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {remainingText}
          </Typography>
        </Box>
      );
    }
  }

  return <>{elements.map((element) => element)}</>;
};

export default TextEdit;
